import React from 'react';
import { Avatar, Box } from '@mui/material';

import Text from 'components/text';
import { CcMatchPreview } from 'hooks/swrRequests/useAdminCcRequests';
import { formatNumberToAbbreviation } from 'utils/helpers';
import type { Artist, UserInfo } from 'types';

import { FONT } from '../constants';
import { getInitials } from '../get-initials';
import Badge from './badge';

export const matchMetaLine = (a: Artist | null): string => {
  if (!a) return '';
  const listeners = a.monthlyListeners ? Number(a.monthlyListeners) : null;
  return [
    a.location,
    listeners != null ? `${formatNumberToAbbreviation(listeners)} listeners` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

// One side of a match: avatar + name + email, and the A&R (name + email) that's CC'd.
const ArtistLine: React.FC<{ artist: Artist; email: string | null; ar: UserInfo | null }> = ({
  artist,
  email,
  ar,
}) => (
  <Box display="flex" alignItems="center" gap={1.25} padding="8px 0">
    <Avatar src={artist.image ?? undefined} sx={{ width: 36, height: 36, fontSize: 12 }}>
      {getInitials(artist.name)}
    </Avatar>
    <Box flex={1} minWidth={0}>
      {/* Row 1: artist name ↔ A&R name */}
      <Box display="flex" alignItems="center" gap={1}>
        <Text
          flex={1}
          minWidth={0}
          fontSize={FONT.base}
          fontWeight={600}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {artist.name}
        </Text>
        {ar?.fullName ? <Badge>{ar.fullName}</Badge> : <Badge variant="outline">Unassigned</Badge>}
      </Box>
      {/* Row 2: artist email ↔ A&R email */}
      <Box display="flex" alignItems="center" gap={1} marginTop="2px">
        {email ? (
          <Text
            flex={1}
            minWidth={0}
            fontSize={FONT.sm}
            color="#64748b"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {email}
          </Text>
        ) : (
          <Text
            flex={1}
            minWidth={0}
            fontSize={FONT.sm}
            fontWeight={600}
            color="#b45309"
            sx={{ whiteSpace: 'nowrap' }}
          >
            No email — won&apos;t be notified
          </Text>
        )}
        {ar?.email && (
          <Text
            fontSize={FONT.sm}
            color="#64748b"
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'right' }}
          >
            {ar.email}
          </Text>
        )}
      </Box>
    </Box>
  </Box>
);

// A single matched pair (artist1 × artist2) with each side's email + A&R. `index`/`total`
// drive an optional "Match N" subheading when several pairs are shown together.
const MatchCard: React.FC<{ match: CcMatchPreview; index?: number; total?: number }> = ({
  match,
  index = 0,
  total = 1,
}) => (
  <Box border="1px solid #e2e8f0" borderRadius="8px" padding="6px 12px" marginBottom="12px">
    {total > 1 && (
      <Text
        fontSize={FONT.sm}
        fontWeight={700}
        color="#94a3b8"
        padding="6px 0 4px"
        marginBottom="2px"
        borderBottom="1px solid #e2e8f0"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        Match {index + 1}
      </Text>
    )}
    <ArtistLine
      artist={match.artist1}
      email={match.artist1User?.email ?? null}
      ar={match.arUser1}
    />
    <Box
      component="div"
      paddingLeft="18px"
      color="#94a3b8"
      fontSize={FONT.sm}
      sx={{ lineHeight: 1 }}
    >
      ×
    </Box>
    <ArtistLine
      artist={match.artist2}
      email={match.artist2User?.email ?? null}
      ar={match.arUser2}
    />
  </Box>
);

export default MatchCard;
